// core/lib/postUrl.ts — the single source of truth for a post's public share URL.
// Mirrors mobile/src/core/lib/postUrl.ts (which hardcodes the prod origin because
// there is no window there); on web the origin is wherever the app is running so
// dev / staging links point back at themselves.
import type { Post } from '../models/post';

/**
 * The five characters `encodeURIComponent` leaves RAW that Python's
 * `quote(..., safe='')` percent-encodes. Everything else the two agree on,
 * `~` included (safe in both since Python 3.7), so this class is the whole
 * difference between the two tiers. See `handleSegment`.
 */
const UNDER_ENCODED_BY_ENCODE_URI_COMPONENT = /[!'()*]/g;

/**
 * Turns a member's handle into the single path segment every link that names a
 * member uses: strip a SINGLE leading `@` (a handle is never `@@name`, and a
 * greedy `/^@+/` would silently rewrite one that legitimately started with a
 * second `@`), then encode what's left so a handle carrying a `/`, a `?` or a
 * non-ASCII character can never break out of the segment its route matches.
 *
 * 1.24.50: extracted so `/profile/<handle>` and `/post/<handle>/<ident>` cannot
 * spell one member's name two different ways. Not exported — the two helpers
 * below are the whole public surface, and a third caller would be a third place
 * for the rule to drift.
 *
 * 1.24.50 fix round — why `encodeURIComponent` alone is not enough:
 *
 *  - `'` TERMINATES A URL IN THIS PLATFORM'S OWN EXTRACTOR. The shared pattern
 *    `https?://[^\s<>"']+` is byte-identical in three places (backend
 *    apps/content/links.py `first_url`, src/components/PostCard/linkify.ts,
 *    mobile's copy) and a sync test pins them together. So a named Copy-link URL
 *    for a member whose handle contains `'` — and the register endpoint accepts
 *    one; there is no charset gate on the username — gets TRUNCATED at the
 *    apostrophe the moment anybody pastes it into a post. The truncated link is
 *    then a different, non-resolving address, and it fails silently: the link
 *    preview describes one thing and the reader clicks another.
 *  - The server builds this same segment with `quote(..., safe='')` (backend
 *    apps/content/seo.py — the canonical `<link rel=canonical>`, `og:url` and
 *    the sitemap). `encodeURIComponent` leaves `! * ' ( )` raw, so without this
 *    second pass the canonical tag and the address bar spell one member's name
 *    two different ways — the exact drift extracting this function fixed
 *    between the two CLIENT links, reappearing between the clients and the
 *    server. All three tiers must agree.
 *
 * Uppercase hex, because `quote` emits uppercase and "agree" here means
 * byte-for-byte, not case-insensitively.
 */
function handleSegment(handle: string): string {
  return encodeURIComponent(handle.replace(/^@/, '')).replace(
    UNDER_ENCODED_BY_ENCODE_URI_COMPONENT,
    (ch) => `%${ch.charCodeAt(0).toString(16).toUpperCase()}`,
  );
}

/**
 * Builds the share URL for a post: the running app's origin followed by
 * `postPath`. Delegating rather than re-deriving is the point — the two used to
 * build the path independently, and a test pins that they agree about which of
 * a post's names is canonical.
 */
export function buildPostShareUrl(
  post: Pick<Post, 'id' | 'shareCode'> & { handle?: string },
): string {
  return `${window.location.origin}${postPath(post)}`;
}

/**
 * The in-app route for a post. Two parts:
 *
 *  - the IDENT — the share code when it has one, the legacy id otherwise. The
 *    detail route resolves BOTH forever, so an old link never dies (1.24.00 D1.1:
 *    `buildPostShareUrl` already preferred the share code, but every link INSIDE
 *    the app still used the legacy id, so the format the owner asked for was
 *    never the one anybody saw).
 *  - the HANDLE — 1.24.50. `/post/WayneTech/XG886741794180` says whose post it
 *    is before you open it. It is PRESENTATIONAL: nothing resolves on it, so a
 *    stale or plain wrong handle still finds the post and is relabelled on
 *    arrival, which is what makes a handle rename harmless.
 *
 * The handle is OPTIONAL, and deliberately so. Several call sites hold nothing
 * but a bare post id — a thread chip, an internal link preview, a notification,
 * a bookmark — and structurally cannot know the post AUTHOR's handle. They emit
 * the bare `/post/<ident>` form, which resolves exactly as it always has and is
 * relabelled by PostDetailPage once the post is in hand. Making the handle
 * required would only push those callers into inventing one.
 */
export function postPath(post: Pick<Post, 'id' | 'shareCode'> & { handle?: string }): string {
  const ident = post.shareCode ?? post.id;
  // A handle of '' or a bare '@' segments to '' — emitting `/post//<ident>` from
  // that would be a broken address, so fall back to the bare form.
  const seg = post.handle ? handleSegment(post.handle) : '';
  return seg ? `/post/${seg}/${ident}` : `/post/${ident}`;
}

/**
 * Builds a member's personal invite link (1.24.00 B2): `/join/<code>`. Same
 * origin rule as post links — the running app's origin, never a hardcoded
 * host — so a dev/staging build never hands out a production link. The code
 * comes from `GET /users/me/invite/` and is URL-safe by construction (a 32-char
 * unambiguous alphabet), so no encoding step is needed; `encodeURIComponent`
 * is applied anyway so a malformed code can never break out of the path.
 */
export function buildJoinUrl(code: string): string {
  return `${window.location.origin}/join/${encodeURIComponent(code)}`;
}

/**
 * Builds a member's public profile URL: `/profile/<handle>`, no leading `@`.
 *
 * 1.24.00 E1. This was previously built inline in four places across the two
 * clients — none of them encoded, so a handle carrying a `/` or `?` (or simply
 * a non-ASCII one) produced a link that pointed somewhere else entirely. One
 * rule, one place — `handleSegment` above, now shared with `postPath` so a
 * member's name is spelled identically in both of the URLs that carry it.
 *
 * Same origin rule as every other link helper here — the running app's origin,
 * never a hardcoded host, so a dev/staging build never hands out a prod link.
 */
export function buildProfileUrl(handle: string): string {
  return `${window.location.origin}/profile/${handleSegment(handle)}`;
}
